/**
 * trendsFormat
 *
 * Shared formatting/parsing helpers for the Trends page (Job Wages &
 * Inflation Comparison), ported VERBATIM from the old Angular
 * SalaryComparisionService's helper methods (salary-comparision.service.ts).
 * All three tab components + the Salary Breakdown dialog use these - keep
 * them in one place so the parity with old Minerva is auditable.
 */

/** null/'NULL'/''/'-' -> null, else parseFloat (NaN -> null). Verbatim parseNum(). */
export function parseNum(val) {
  if (val == null || val === "NULL" || val === "" || val === "-") return null;
  const n = parseFloat(val);
  return isNaN(n) ? null : n;
}

/** GBP currency, 0 decimals, em dash for non-numbers. Verbatim fmt(). */
export function fmt(val) {
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(n);
}

/** "+1.2%" / "-3.4%" / em dash. Verbatim fmtPct(). */
export function fmtPct(val) {
  const n = parseNum(val);
  if (n === null) return "—";
  return `${n > 0 ? "+" : ""}${n.toFixed(1)}%`;
}

/**
 * CSS class for a pay/real-change cell: green if >0, red if <0, none at 0 or
 * null. Same rule as the old payChangeClass() (its class names, restyled in
 * TrendsPage.css).
 */
export function payChangeClass(val) {
  const n = parseNum(val);
  if (n === null) return "";
  return n > 0 ? "tr-pos" : n < 0 ? "tr-neg" : "";
}

/**
 * Parse a review-period label like "Jan 21-Dec 21" into the pieces the
 * Salary Breakdown dialog's request needs (month NAMES + full years).
 * Verbatim parseReviewPeriod() - this, not the numeric window start month,
 * is the old contract for GetAvgSalaryDataForTimePeriod.
 */
export function parseReviewPeriod(row, startYear, endYear) {
  const parts = (row.review_period || "").split("-");
  const [startMon, startYY] = (parts[0] ?? "").trim().split(" ");
  const [endMon, endYY] = (parts[1] ?? "").trim().split(" ");
  const fullStartYear = startYY ? 2000 + parseInt(startYY, 10) : startYear ?? 0;
  const fullEndYear = endYY ? 2000 + parseInt(endYY, 10) : endYear ?? 0;
  return { startMon, endMon, fullStartYear, fullEndYear };
}

/** Fixed month options for the Review Period dropdown (1-12, Jan default). */
export const MONTH_OPTIONS = [
  { label: "Jan", value: 1 }, { label: "Feb", value: 2 }, { label: "Mar", value: 3 },
  { label: "Apr", value: 4 }, { label: "May", value: 5 }, { label: "Jun", value: 6 },
  { label: "Jul", value: 7 }, { label: "Aug", value: 8 }, { label: "Sep", value: 9 },
  { label: "Oct", value: 10 }, { label: "Nov", value: 11 }, { label: "Dec", value: 12 },
];

/**
 * Forecast palette - the old charts' exact amber (Chart.js rgba fills,
 * #ffc107 border) so projected bars/rows read identically.
 */
export const FORECAST = {
  barFillStrong: "rgba(255,193,7,0.50)",
  barFillSoft: "rgba(255,193,7,0.25)",
  border: "#ffc107",
};
